import { LOGIN_TYPE } from '@dni-analysis/types/cosign-events'
import { type Liff } from '@line/liff'
import { useCallback, useMemo, useState } from 'react'
import { type Otp, type ThirtyPartyLoginType } from '../types/dataModel/platformSdk'
import { wordPressHost } from '../utils/location'
import { getRegisterOrLoginRedirectUrl } from '../utils/wordPressRoute'
import { useRequestLoginByFacebook } from './apis/wp/useRequestLoginByFacebook'
import { useRequestLoginByGoogle } from './apis/wp/useRequestLoginByGoogle'
import { useRequestLoginByLine } from './apis/wp/useRequestLoginByLine'

const THIRD_PARTY_FALLBACK_QUERY_MAP = {
  line: `${wordPressHost}/my-account/?loginByLine=false`,
  facebook: `${wordPressHost}/my-account/?loginByFacebook=false`,
  google: `${wordPressHost}/my-account/?loginByGoogle=false`,
} as const

interface UseThirtyPartyBindIdentityProps {
  onError?: () => void
  redirectUrl?: Otp['redirectUrl']
  fallbackUrl?: Otp['fallbackUrl']
  tokenLoginUrl?: Otp['tokenLoginUrl']
  liff?: Liff
}

export function useThirtyPartyLogin(props?: UseThirtyPartyBindIdentityProps) {
  const [unbindType, setUnbindType] = useState<ThirtyPartyLoginType | null>()
  const { trigger: triggerLoginByLine, isMutating: isMutatingLoginByLine } = useRequestLoginByLine()
  const { trigger: triggerByLoginByFacebook, isMutating: isMutatingLoginByFacebook } = useRequestLoginByFacebook()
  const { trigger: triggerByLoginByGoogle, isMutating: isMutatingLoginByGoogle } = useRequestLoginByGoogle()
  const isMutating = isMutatingLoginByLine || isMutatingLoginByFacebook || isMutatingLoginByGoogle

  const THIRD_PARTY_TRIGGER_MAP = useMemo(() => ({
    line: triggerLoginByLine,
    facebook: triggerByLoginByFacebook,
    google: triggerByLoginByGoogle,
  }) as const, [triggerLoginByLine, triggerByLoginByFacebook, triggerByLoginByGoogle])

  const loginByThirtyParty = useCallback(async (type: ThirtyPartyLoginType) => {
    const redirectUrl = getRegisterOrLoginRedirectUrl()
    try {
      setUnbindType(type)
      const { data } = await THIRD_PARTY_TRIGGER_MAP[type]({
        redirectUrl: props?.redirectUrl || redirectUrl,
        fallbackUrl: props?.fallbackUrl || THIRD_PARTY_FALLBACK_QUERY_MAP[type],
        tokenLoginUrl: props?.tokenLoginUrl,
      })
      // NOTE: 因為 Google 限制在 LIFF 內無法 oauth，因此需要 openWindow
      if (type === LOGIN_TYPE.GOOGLE && props?.liff?.isInClient()) {
        props.liff.openWindow({ url: data.data.url, external: true })
      }
      else {
        window.location.href = data.data.url
      }
    }
    catch (e) {
      props?.onError?.()
      console.error(e)
    }
  }, [THIRD_PARTY_TRIGGER_MAP, props])

  const loginByLine = useCallback(() => loginByThirtyParty(LOGIN_TYPE.LINE), [loginByThirtyParty])
  const loginByFacebook = useCallback(() => loginByThirtyParty(LOGIN_TYPE.FACEBOOK), [loginByThirtyParty])
  const loginByGoogle = useCallback(() => loginByThirtyParty(LOGIN_TYPE.GOOGLE), [loginByThirtyParty])

  return {
    loginByLine,
    loginByFacebook,
    loginByGoogle,
    isMutating,
    unbindType,
  }
}
